import { eq, type InitialQueryBuilder } from "@tanstack/db";
import { useLiveQuery } from "@tanstack/react-db";
import React, { createContext, useContext, useEffect } from "react";

import { setSessionAttribute } from "@/services/telemetry";
import { store } from "@/store/store";
import { Account } from "@/store/types";
import { TelemetryContext } from "../TelemetryProvider";

type MyAccountState = {
  myAccount?: Account;
  myAccountId?: string;
  setMyAccount: (acct: Account) => void;
};

export const MyAccountContext = createContext<MyAccountState>({
  setMyAccount: (_acct: Account) => void {},
});

const myAccountQuery = (q: InitialQueryBuilder) =>
  q.from({ account: store.collection("account") }).where(({ account }) => eq(account.mine, true));

const MyAccountProvider = ({ children }) => {
  const [myAccountId, setMyAccountId] = React.useState<string | undefined>(undefined);
  const [myAccount, setMyAccount] = React.useState<Account>(undefined);
  const { setAttribute } = useContext(TelemetryContext);

  const { data: myAccounts } = useLiveQuery(myAccountQuery);
  const localMyAccount = myAccounts?.[0];

  useEffect(() => {
    setMyAccount(localMyAccount);
    if (localMyAccount) {
      setAttribute("accountId", localMyAccount.id);
      // Authoritative refresh of the OTel session attribute once the local account record resolves;
      // it was seeded from the auth session at boot so early spans are already attributed.
      setSessionAttribute("account.id", localMyAccount.id);
      setMyAccountId(localMyAccount.id);
    }
  }, [localMyAccount]);

  return (
    <MyAccountContext.Provider value={{ myAccountId, myAccount, setMyAccount }}>{children}</MyAccountContext.Provider>
  );
};

export default MyAccountProvider;
